import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEdit,
  faTrash,
  faFileAlt,
  faFileImage,
  faFilePdf,
  faFileArchive,
  faPaperclip,
  faEye
} from '@fortawesome/free-solid-svg-icons';
import ApiServices from '../../Services/ApiServices';
import { getFullFileUrl } from '../../../Helpers/UrlHelpers';
import DynamicModal from '../../Shared/DynamicModal';
import DeleteAlert from '../../Shared/DeleteAlert';
import FileViewer from '../../Shared/FileViewer';
import MiniLoader from '../../Shared/MiniLoader';
import { toastHelper } from '../../../Helpers/toastHelper';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../../../contexts/LanguageContext';
import htmlHelper from '../../../Helpers/htmlHelper';

const getFileIcon = (url = '') => {
  const ext = url.split('.').pop()?.toLowerCase();
  if (['png', 'jpg', 'jpeg', 'svg', 'webp', 'gif'].includes(ext)) return faFileImage;
  if (ext === 'pdf') return faFilePdf;
  if (['zip', 'rar', '7z'].includes(ext)) return faFileArchive;
  return faFileAlt;
};

const isImageFile = (url = '') => {
  const ext = url.split('.').pop()?.toLowerCase();
  return ['png', 'jpg', 'jpeg', 'svg', 'webp', 'gif'].includes(ext);
};


const CourseVideoAttachments = ({ courseVideoId }) => {
  const { t } = useLanguage();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  // Edit Modal State
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  // Delete Alert State
  const [deleteAlertOpen, setDeleteAlertOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // File Viewer State
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerIndex, setViewerIndex] = useState(0);

  const fetchAttachments = useCallback(async () => {
    if (!courseVideoId) return;
    setLoading(true);
    try {
      const response = await ApiServices.get(`CourseVideoAttachment/getAll/${courseVideoId}`);
      setData(response.data || response.data_list || []);
    } catch (error) {
      toastHelper.error(t('cva_loadError') || 'تعذر تحميل مرفقات الفيديو');
    } finally {
      setLoading(false);
    }
  }, [courseVideoId, t]);

  useEffect(() => {
    fetchAttachments();
  }, [fetchAttachments]);

  const viewerFiles = useMemo(() => {
    return data.map((item) => ({
      id: item.id,
      file_name: item.attachment_url,
      title: item.title || item.description,
      description: item.description,
    }));
  }, [data]);

  const handleOpenViewer = (index) => {
    setViewerIndex(index);
    setViewerOpen(true);
  };

  const handleEditClick = (item) => {
    setEditingItem(item);
    setEditModalOpen(true);
  };

  const handleUpdateSubmit = async (formData) => {
    if (!editingItem) return;
    try {
      const payload = {
        title: formData.title || '',
        description: formData.description || '',
      };
      await ApiServices.put(`CourseVideoAttachment/update/${editingItem.id}`, payload);
      toastHelper.success(t('cva_updateSuccess') || 'تم تحديث المرفق بنجاح');
      setEditModalOpen(false);
      setEditingItem(null);
      fetchAttachments();
    } catch (error) {
      toastHelper.error(error?.msg || t('cva_updateError') || 'فشل تحديث بيانات المرفق');
      throw error;
    }
  };

  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setDeleteAlertOpen(true);
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;
    setDeleteLoading(true);
    try {
      await ApiServices.delete(`CourseVideoAttachment/delete/${itemToDelete.id}`);
      toastHelper.success(t('cva_deleteSuccess') || 'تم حذف المرفق بنجاح');
      setViewerOpen(false);
      fetchAttachments();
    } catch (error) {
      toastHelper.error(error?.msg || t('cva_deleteError') || 'تعذر حذف المرفق');
    } finally {
      setDeleteLoading(false);
      setDeleteAlertOpen(false);
      setItemToDelete(null);
    }
  };

  const editFormConfig = [
    {
      name: 'title',
      label: t('cva_titleLabel') || 'عنوان المرفق',
      type: 'text',
      required: true,
      defaultValue: editingItem?.title || editingItem?.description || '',
    },
    {
      name: 'description',
      label: t('cva_descLabel') || 'الوصف (توضيح تفاصيل المرفق)',
      type: 'text',
      multiline: true,
      rows: 3,
      required: false,
      defaultValue: editingItem?.description || '',
    },
  ];

  if (loading && !data.length) {
    return (
      <div style={{ padding: '3rem', textAlign: 'center' }}>
        <MiniLoader size={32} color="#6366f1" />
        <p style={{ marginTop: '1rem', color: '#64748b', fontWeight: 600 }}>
          {t('loadingData') || 'جاري تحميل المرفقات...'}
        </p>
      </div>
    );
  }

  if (!loading && data.length === 0) {
    return (
      <div className="cva-empty-container">
        <div className="cva-empty-icon">
          <FontAwesomeIcon icon={faPaperclip} />
        </div>
        <h4>{t('cva_noAttachmentsYet') || 'لا توجد مرفقات مضافة لهذا الفيديو بعد'}</h4>
        <p>{t('cva_addAttachmentHint') || 'يمكنك إضافة ملفات ومستندات وصور مرفقة من زر إضافة مرفق بطاقة الفيديو'}</p>

        <style jsx>{`
          .cva-empty-container {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            padding: 3.5rem 1.5rem;
            text-align: center;
          }
          .cva-empty-icon {
            width: 72px;
            height: 72px;
            border-radius: 24px;
            background: #f1f5f9;
            color: #94a3b8;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 2rem;
            margin-bottom: 1.25rem;
          }
          .cva-empty-container h4 {
            margin: 0 0 0.5rem 0;
            font-size: 1.2rem;
            font-weight: 700;
            color: #334155;
          }
          .cva-empty-container p {
            margin: 0;
            font-size: 0.95rem;
            color: #64748b;
            max-width: 420px;
          }
        `}</style>
      </div>
    );
  }

  return (
    <div className="cva-grid-wrapper">
      <div className="cva-grid">
        <AnimatePresence>
          {data.map((item, idx) => {
            const fileUrl = getFullFileUrl(item.attachment_url);
            const isImg = isImageFile(item.attachment_url);

            return (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, scale: 0.95, y: 15 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.25 }}
                className="cva-card glass-card"
              >
                {/* Preview / Header */}
                <div
                  className="cva-preview-box"
                  onClick={() => handleOpenViewer(idx)}
                  style={{ cursor: 'pointer' }}
                >
                  {isImg ? (
                    <img src={fileUrl} alt={htmlHelper.stripHtml(item.title || item.description)} className="cva-img-preview" />
                  ) : (
                    <div className="cva-file-placeholder">
                      <FontAwesomeIcon icon={getFileIcon(item.attachment_url)} className="cva-big-icon" />
                    </div>
                  )}

                  <button
                    type="button"
                    className="cva-open-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenViewer(idx);
                    }}
                    title={t('cva_downloadFile') || 'معاينة / فتح الملف'}
                  >
                    <FontAwesomeIcon icon={faEye} />
                  </button>
                </div>

                {/* Info */}
                <div className="cva-card-body">
                  <h4 className="cva-card-title">{htmlHelper.stripHtml(item.title || item.description || 'ملف مرفق')}</h4>
                  {item.description && item.title && (
                    <p className="cva-card-desc">{htmlHelper.stripHtml(item.description)}</p>
                  )}

                  {/* Actions */}
                  <div className="cva-card-actions">
                    <button
                      className="cva-btn cva-btn-edit"
                      onClick={() => handleEditClick(item)}
                      title={t('edit') || 'تعديل'}
                    >
                      <FontAwesomeIcon icon={faEdit} />
                      <span>{t('edit') || 'تعديل'}</span>
                    </button>
                    <button
                      className="cva-btn cva-btn-delete"
                      onClick={() => handleDeleteClick(item)}
                      title={t('delete') || 'حذف'}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                      <span>{t('delete') || 'حذف'}</span>
                    </button>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>

      {/* FileViewer Fullscreen Modal */}
      {viewerOpen && viewerFiles.length > 0 && (
        <FileViewer
          isOpen={viewerOpen}
          onClose={() => setViewerOpen(false)}
          files={viewerFiles}
          currentIndex={Math.min(viewerIndex, viewerFiles.length - 1)}
        />
      )}

      {/* Edit Dynamic Modal */}
      <DynamicModal
        isOpen={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setEditingItem(null);
        }}
        title={t('cva_editAttachmentTitle') || 'تعديل بيانات المرفق'}
        formConfig={editFormConfig}
        onSubmit={handleUpdateSubmit}
        submitButtonText={t('save') || 'حفظ'}
      />

      {/* Delete Confirmation Alert */}
      <DeleteAlert
        isOpen={deleteAlertOpen}
        onClose={() => {
          setDeleteAlertOpen(false);
          setItemToDelete(null);
        }}
        onConfirm={confirmDelete}
        title={t('cva_deleteAttachmentConfirmTitle') || 'تأكيد حذف المرفق'}
        message={t('cva_deleteAttachmentConfirmMessage') || 'هل أنت متأكد من حذف هذا المرفق نهائياً؟'}
        itemName={htmlHelper.stripHtml(itemToDelete?.title || itemToDelete?.description)}
        loading={deleteLoading}
      />

      <style jsx>{`
        .cva-grid-wrapper {
          padding: 0.5rem 0;
        }
        .cva-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 1.25rem;
        }
        .cva-card {
          background: #ffffff;
          border-radius: 20px;
          border: 1px solid #e2e8f0;
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.04);
          overflow: hidden;
          display: flex;
          flex-direction: column;
          transition: all 0.3s ease;
        }
        .cva-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 30px rgba(0, 0, 0, 0.08);
          border-color: #cbd5e1;
        }
        .cva-preview-box {
          position: relative;
          height: 150px;
          background: #f8fafc;
          border-bottom: 1px solid #f1f5f9;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
        }
        .cva-img-preview {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        .cva-file-placeholder {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 60px;
          height: 60px;
          border-radius: 18px;
          background: #e0e7ff;
          color: #4f46e5;
        }
        .cva-big-icon {
          font-size: 1.75rem;
        }
        .cva-open-btn {
          position: absolute;
          top: 12px;
          left: 12px;
          width: 36px;
          height: 36px;
          border-radius: 10px;
          background: rgba(255, 255, 255, 0.9);
          backdrop-filter: blur(8px);
          color: #334155;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.9rem;
          box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
          transition: all 0.2s ease;
          border: none;
          cursor: pointer;
        }
        .cva-open-btn:hover {
          background: #6366f1;
          color: #fff;
          transform: scale(1.08);
        }
        .cva-card-body {
          padding: 1.25rem;
          display: flex;
          flex-direction: column;
          flex: 1;
          justify-content: space-between;
        }
        .cva-card-title {
          margin: 0 0 0.4rem 0;
          font-size: 1.05rem;
          font-weight: 700;
          color: #1e293b;
          line-height: 1.4;
        }
        .cva-card-desc {
          margin: 0 0 1rem 0;
          font-size: 0.88rem;
          color: #64748b;
          line-height: 1.5;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .cva-card-actions {
          display: flex;
          gap: 0.75rem;
          margin-top: 0.75rem;
          padding-top: 0.75rem;
          border-top: 1px solid #f1f5f9;
        }
        .cva-btn {
          flex: 1;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 0.4rem;
          padding: 0.5rem 0.75rem;
          border-radius: 10px;
          border: 1px solid transparent;
          font-size: 0.85rem;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.2s ease;
          font-family: inherit;
        }
        .cva-btn-edit {
          background: #f1f5f9;
          color: #475569;
          border-color: #e2e8f0;
        }
        .cva-btn-edit:hover {
          background: #3b82f6;
          color: #fff;
          border-color: #3b82f6;
        }
        .cva-btn-delete {
          background: #fef2f2;
          color: #dc2626;
          border-color: #fecaca;
        }
        .cva-btn-delete:hover {
          background: #ef4444;
          color: #fff;
          border-color: #ef4444;
        }
      `}</style>
    </div>
  );
};

export default CourseVideoAttachments;
